import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  ScrollArea,
  Box,
  Center,
  Loader,
  Group,
  Text,
  Grid,
  Image,
  createStyles,
  Button,
  Textarea,
  Progress,
  Alert,
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import SpaceModel from "../../../models/Space";
import VisionModel from "../../../models/Vision";

import VisionChannel from "./VisionChannel";

import defaultSpaceColors from "../../../collections/defaultSpaceColors";
import { setColors } from "../../../store/app";
import isSpaceThemed from "../../../utils/IsSpaceThemed";
import trackMixpanel from "../../../utils/TrackMixpanel";
import { getRoot } from "../../../utils/Host";
import transformVisionToTiptap from "../../../utils/transformVisionToTiptap";

import TiptapNoHP from "./TiptapNoHP";

const useStyles = createStyles(() => ({
  sidebarItem: {
    padding: "10px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#fafafa",
    },
  }
}));

const templates = [
  { id: "recommended", title: "Recommended", type: "recommended" },
  { id: "single_page", title: "Single page course", type: "single_page" },
  { id: "multi_page", title: "Multi page course", type: "multi_page" },
  { id: "worksheet", title: "Worksheet", type: "worksheet" },
  { id: "online_summit", title: "Online summit", type: "online_summit" },
  { id: "cohort_course", title: "Cohort course", type: "cohort_course" },
  { id: "event", title: "Event", type: "event" }
];

const AIBuilderModal = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const colors = useSelector((state) => state.colors);

  const { classes } = useStyles();

  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [vision, setVision] = useState(null);
  const [space, setSpace] = useState(null);
  const [aiPromptValue, setAIPromptValue] = useState("");
  const [isGenerated, setIsGenerated] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);

  const [uploading, setUploading] = useState(false);

  const getTemplateGenerationStatus = () => {
    if (!vision || !vision.structures || !vision.structures[selectedTemplate.type]) {
      return 'generating';
    }
    
    const templateStructure = vision.structures[selectedTemplate.type];
    
    if (templateStructure?.status === 'structure_completed') {
      return 'completed';
    } else if (templateStructure?.status === 'error') {
      return 'error';
    } else if (templateStructure?.status === 'structure') {
      return 'generating';
    }
    
    return 'generating';
  };

  const tiptapContent = transformVisionToTiptap(vision, selectedTemplate.type);
  const existsContent = Array.isArray(tiptapContent?.content);

  const btnBgColor = selectedTheme?.primary_button_background_color || undefined;
  const btnTextColor = selectedTheme?.primary_button_text_color || undefined;
  const isComponent = props.isComponent;
  const showAiPrompt = isComponent && !isGenerated;

  // useEffect(() => {
  //   onFetchVision();
  //   window.$spaceUserInteractions = {};
  // }, [])

  // const onFetchVision = () => {
  //   VisionModel.onFetch(
  //     158,
  //     (data) => {
  //       console.log("DATA::: ", data);
  //       setVision(data);
  //     },
  //     (data) => {}
  //   );
  // };

  useEffect(() => {
    if (props.space) {
      setSpace(props.space);
      if (isComponent && colors && isSpaceThemed(colors)) {
        setSelectedTheme(colors);
      }
      trackMixpanel(user.username, "Vision template selected");
    } else {
      onCreateVision();
      onFetchThemes();
      trackMixpanel(user.username, "Vision builder selected");
    }
    window.$spaceUserInteractions = {};
    return () => {
      if (!isComponent) {
        dispatch(setColors(defaultSpaceColors));
      }
    }
  }, []);

  useEffect(() => {
    if (aiPromptValue.length && isGenerated && !vision) {
      onCreateVision(aiPromptValue);
    }
  }, [aiPromptValue, isGenerated, vision]);

  const onCreateSpace = () => {
    setUploading(true);
    if (space) {
      onUseTemplate(space.slug);
    } else {
      const spaceParams = {
        intention: tiptapContent?.intention || "VISION",
        version: "content",
        privacy_level: "open",
        join_on_create: true,
        launch_on_date: null,
        launch_time: null,
        show_start_date: false,
        start_date: new Date(new Date().getTime() + 86400000),
        end_date: new Date(new Date().getTime() + 90000000),
        start_time: new Date(new Date().getTime() + 86400000),
        end_time: new Date(new Date().getTime() + 90000000),
        content: "",
        hangout_link: "",
      };
      SpaceModel.onCreate(
        spaceParams,
        null,
        (data) => {
          setSpace(data);
          onUseTemplate(data.slug);
          if (selectedTheme) {
            onSaveTheme(selectedTheme, data.slug);
          }
        },
        (data) => {}
      );
    }
  };

  const onCreateVision = (value) => {
    console.log("onCreateVision called with:", value);
    
    VisionModel.onCreate(
      value || props.aiPrompt,
      null,
      (data) => {
        console.log("Vision created successfully:", data);
        setVision(data);
        // Start generating structures for all templates
        generateAllStructures(data.id);
      },
      (error) => {
        console.log("Vision creation failed:", error);
      }
    );
  }

  const generateAllStructures = async (visionId) => {
    const generationPromises = templates.filter(template => template.type !== "cohort_course").map(async (template) => {
      console.log(`Starting generation for template: ${template.type}`);
      
      return new Promise((resolve, reject) => {
        VisionModel.onCreateStructure(
          visionId,
          template.type,
          (data) => {
            console.log(`Structure created for ${template.type}:`, data);
            setVision(data.vision);
            resolve({ template: template.type, success: true, data });
          },
          (error) => {
            console.log(`Structure creation failed for ${template.type}:`, error);
            resolve({ template: template.type, success: false, error });
          }
        );
      });
    });
    
    try {
      // Wait for all generations to complete
      const results = await Promise.all(generationPromises);
      
      console.log('All template generations completed:', results);
      
      // Check if any generations failed
      const failedTemplates = results.filter(result => !result.success);
      if (failedTemplates.length > 0) {
        console.log('Some templates failed to generate:', failedTemplates);
      }
      
    } catch (error) {
      console.log('Error in parallel generation:', error);
    }
  }

  const onRetryGeneration = () => {
    if (!vision?.id) return;
    
    // Reset the status for this template to 'generating'
    const currentStructures = vision.structures || {};
    currentStructures[selectedTemplate.type] = {
      ...currentStructures[selectedTemplate.type],
      status: 'structure'
    };
    setVision({ ...vision, structures: currentStructures });
    
    VisionModel.onRetry(
      vision.id,
      selectedTemplate.type,
      (data) => {
        setVision(data.vision);
      },
      (error) => {
        console.log("Retry failed:", error);
      }
    );
  }
  
  const onFetchThemes = () => {
    SpaceModel.onFetchThemes(
      (data) => {
        setThemes(data);
      },
      (data) => {}
    );
  };

  const onUseTemplate = (space_slug) => {
    VisionModel.onUseTemplate(
      vision.id,
      space_slug,
      selectedTemplate.type,
      (data) => {
        setUploading(false);
        dispatch(setColors(defaultSpaceColors));
        if (data.redirect_url) {
          window.location = `${getRoot()}${data.redirect_url}`;
          if (isComponent) {
            window.location.reload();
          }
        }
      },
      (data) => {
        setUploading(false);
      }
    );
  };

  const onSelectedTheme = (theme) => {
    setSelectedTheme(theme);
    dispatch(setColors({...theme, is_vision_builder: true}));
  };

  const onResetTheme = () => {
    setSelectedTheme(null);
    dispatch(setColors(defaultSpaceColors));
  }

  const onSaveTheme = (theme, space_slug) => {
    SpaceModel.onSaveColors(
      space_slug,
      theme,
      (data) => {},
      (data) => {}
    );
  };

  const getGenerationStatusText = () => {
    const templateStatus = getTemplateGenerationStatus();
    
    switch (templateStatus) {
      case 'generating':
        return "Creating structure...";
      case 'completed':
        return "Structure completed";
      case 'error':
        return "Generation failed";
      default:
        return "";
    }
  };

  const ModalOrView = isComponent ? Box : Modal;

  if (isComponent && !props.opened) {
    return null;
  }

  return (
    <ModalOrView
      styles={isComponent ? null : { inner: { overflow: "hidden", height: "90vh", marginTop: 30 } }}
      withCloseButton={false}
      opened={props.opened}
      onClose={() => props.setOpened(false)}
      padding={0}
      size="90%"
      zIndex={99999}
    >
      <Grid gutter={0} columns={showAiPrompt ? 9 : 12}>
        {!showAiPrompt && (
          <Grid.Col span={3} sx={{ borderRight: "1px solid #f1f3f5" }}>
            <Box pl={20} pr={20} sx={{ height: "85vh", display: 'flex', flexDirection: 'column' }}>

              {isComponent ? (
                <Box mt={10}></Box>
              ) : (
                <Text size="xl" weight={600} mt={20} mb={10}>{"Templates"}</Text>
              )}

              <Box my={20} style={{ width: '100%', height: 1, backgroundColor: "#f1f3f5" }}></Box>

              {templates.map((template, index) => {
                // Check status for this specific template
                const getTemplateStatus = () => {
                  if (!vision || !vision.structures || !vision.structures[template.type]) {
                    return 'generating';
                  }
                  
                  const templateStructure = vision.structures[template.type];
                  
                  if (templateStructure?.status === 'structure_completed') {
                    return 'completed';
                  } else if (templateStructure?.status === 'error') {
                    return 'error';
                  } else if (templateStructure?.status === 'structure') {
                    return 'generating';
                  }
                  
                  return 'generating';
                };
                
                const templateStatus = getTemplateStatus();
                const isGenerating = templateStatus === 'generating';
                const isCompleted = templateStatus === 'completed';
                const hasError = templateStatus === 'error';
                
                return (
                  <Group
                    noWrap
                    className={classes.sidebarItem}
                    onClick={() => setSelectedTemplate(template)}
                    align="top"
                    disabled={showAiPrompt}
                  >
                    <Box width={24} height={20}>
                      {isGenerating ? (
                        <Loader size={15} mt={5} />
                      ) : isCompleted ? (
                        <FontAwesomeIcon size={16} icon={solid("check-circle")} color="green" />
                      ) : hasError ? (
                        <FontAwesomeIcon size={16} icon={solid("exclamation-triangle")} color="red" />
                      ) : (
                        <FontAwesomeIcon size={16} icon={solid("bolt")} />
                      )}
                    </Box>
                    <Text align="left" weight={600}>{template.title}</Text>
                  </Group>
                );
              })}

              {!isComponent && (
                <>
                  <Box sx={{ flex: isComponent ? 0 : 1 }} />

                    <Group sx={{ justifyContent: "space-between" }} spacing={0}>
                      <Text size="xl" weight={600} mt={20} mb={20}>{t("spaces.colors.form.colorTheme")}</Text>
                      {!!selectedTheme && (
                        <Button radius="xl" color="dark" size="xs" onClick={onResetTheme}>
                          {"Reset"}
                        </Button>
                      )}
                    </Group>
                    <Grid columns={9}>
                      {Object.keys(themes).map((theme) => {
                        return (
                          <Grid.Col xs={3}>
                            <Box
                              class={`${selectedTheme?.id == themes[theme].id ? "theme-selected" : ""}`}
                              onClick={(event) => {
                                if (!showAiPrompt) {
                                  onSelectedTheme(themes[theme]);
                                }
                              }}
                            >
                              <Image
                                radius="md"
                                src={`../../../themes/${themes[theme].file_name}`}
                                alt={themes[theme].title}
                              />
                            </Box>
                          </Grid.Col>
                        );
                      })}
                    </Grid>
                </>
              )}

            </Box>
          </Grid.Col>
        )}

        {showAiPrompt ? (
          <Grid.Col span={9} sx={{ height: "85vh" }} align="center">
            <Box sx={{ width: "50%" }} mt={100}>
              <Textarea
                label={"What do you want to create?"}
                placeholder={"Write something..."}
                data-autofocus
                value={aiPromptValue}
                onChange={(event) => setAIPromptValue(event.currentTarget.value)}
                minRows={3}
                maxRows={3}
                radius="md"
                styles={{ label: { fontWeight: "600", fontSize: 18, paddingBottom: 10 } }}
              />

              <Progress
                value={aiPromptValue.length * 2}
                mt={20}
              />

              <Text color="dimmed" pt={15}>
                {"Our AI Vision Builder can help you create the perfect template for your project.  Please share a sentence or more about what you have in mind."}
              </Text>

              <Button
                mt={20}
                mb={10}
                // fullWidth
                radius="md"
                size="lg"
                disabled={aiPromptValue.length >= 25 && aiPromptValue.length <= 1000 ? false : true}
                leftIcon={<FontAwesomeIcon icon={solid("wand-magic-sparkles")} size="md" />}
                onClick={() => {
                  console.log("Start generating button clicked");
                  console.log("aiPromptValue length:", aiPromptValue.length);
                  setIsGenerated(true);
                }}
              >
                {"Start generating"}
              </Button>
            </Box>
          </Grid.Col>
        ) : (
          <Grid.Col span={9} sx={{ backgroundColor: selectedTheme?.header_color }}>
            <Box sx={{ position: "relative", width: "100%", zIndex: 99999999 }}>
              <Box sx={{ width: "100%", paddingLeft: 40, paddingRight: 40, borderBottom: selectedTheme ? null : "1px solid #f1f3f5" }}>
                <Box
                  pt={20}
                  mt={10}
                  pb={30}
                >
                  <Group position="apart">
                    <Text weight="700" size="lg" color={selectedTheme?.header_link_color} sx={{ flex: 1 }}>
                      {tiptapContent?.intention || "AI Generated Content"}
                    </Text>
                    <Group position={"right"}>
                      {getTemplateGenerationStatus() === 'completed' && (
                        <>
                          {uploading ? (
                            <>
                              <Text color={selectedTheme?.header_link_color}>Loading...</Text>
                              <Loader size={20} />
                            </>
                          ) : (
                            <Button radius="xl" sx={{ backgroundColor: btnBgColor, color: btnTextColor }} onClick={onCreateSpace}>
                              {"Use template"}
                            </Button>
                          )}
                        </>
                      )}
                    </Group>
                  </Group>
                </Box>
              </Box>
            </Box>
            
            <ScrollArea style={{ height: "85vh", backgroundColor: selectedTheme?.background_color }}>
              <Box pb={30}></Box>
              <Box sx={{ paddingLeft: 24, paddingRight: 24 }}>
                {getTemplateGenerationStatus() === 'generating' && (
                  <Center mb={20}>
                    <Box align="center">
                      <Loader size="lg" />
                      <Text pt={10} color={selectedTheme?.default_text_color}>
                        {getGenerationStatusText()}
                      </Text>
                    </Box>
                  </Center>
                )}

                {getTemplateGenerationStatus() === 'error' && (
                  <Alert 
                    icon={<FontAwesomeIcon icon={solid("exclamation-triangle")} />}
                    title="Generation failed"
                    color="red"
                    mb={20}
                  >
                    <Text size="sm" mb={10}>{"Failed to generate structure for this template"}</Text>
                    <Button size="sm" onClick={onRetryGeneration}>
                      {"Retry"}
                    </Button>
                  </Alert>
                )}

                {existsContent && (
                  <>
                    {tiptapContent.content.length > 0 ? (
                      <TiptapNoHP
                        key={uploading ? "tiptap-uploading" : `tiptap-${selectedTemplate.type}`}
                        content={tiptapContent}
                        isAIBuilder={true}
                      />
                    ) : (
                      <Box mb={isComponent ? 100 : 180}></Box>
                    )}
                  </>
                )}
                {getTemplateGenerationStatus() === 'generating' && !existsContent && (
                  <Center>
                    <Box align="center">
                      <Loader size="lg" />
                      <Text pt={10} color={selectedTheme?.default_text_color}>
                        {"Generating structure..."}
                      </Text>
                    </Box>
                  </Center>
                )}
              </Box>
            </ScrollArea>
          </Grid.Col>
        )}
      </Grid>

      {!!user?.id && (
        <VisionChannel
          user_id={user.id}
          onStructureCreated={(data) => {
            console.log("Structure created event:", data);
            if (data && data.vision) {
              setVision(data.vision);
            }
          }}
          onError={(data) => {
            console.log("Structure error event:", data);
          }}
        />
      )}

    </ModalOrView>
  );
};

export default AIBuilderModal;
